import { throttle } from 'es-toolkit';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box2, Vector2, Vector3 } from 'three';

import { Component, Member, PlatePair } from '@paragon/contract-library';
import { angleEquals, convertDegreesToRadians, convertRadiansToDegrees } from '@paragon/geometry-library';
import { calculateMemberMidline, newVector3, RenderOrder, Segment3D } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { findMembersWhichTouchPlatePair } from '../../library/geometryUtility/findMembersWhichTouchPlatePair';
import { box2FromSize } from '../../library/utilities/box2FromSize';
import { setEditInProgress } from '../../store/actions/editorActions';
import { getCurrentCoordinateDirection } from '../../store/selectors/getCurrentCoordinateDirectionSelector';
import { upsertPlatePairs } from '../../store/slices/componentDesignsSlice';
import { analyzeSelectedPlates } from '../../store/slices/livePlatingSlice';
import { NudgeHandle } from './NudgeHandle';
import { RotateHandle } from './RotateHandle';

interface Props {
  readonly platePair: PlatePair;
  readonly component: Component;
  readonly componentDesignGuid: string;
  readonly setEditedComponent: (component: Component) => void;
}

export function PlatePairEditorHandle({ platePair, component, componentDesignGuid, setEditedComponent }: Props) {
  const dispatch = useAppDispatch();

  const coordinateUpDirection = useAppSelector(getCurrentCoordinateDirection);

  const [angleSnap, setAngleSnap] = useState<ReturnType<typeof snapAngle>['snapTo']>(null);
  const [snappedAngle, setSnappedAngle] = useState(platePair.angleZ);

  // We store the most recent plate pair in a ref so that we can use it when flushing the changes to Redux. The
  // `component` will not necessarily be up to date with the latest changes from a move callback, since React batches
  // callbacks.
  const editedPlatePairRef = useRef<PlatePair | null>(null);

  const throttledAnalyzeSelectedPlates = useMemo(
    () => throttle((component: Component) => dispatch(analyzeSelectedPlates(undefined, component)), 250),
    [dispatch],
  );

  const updateEditedPlatePair = useCallback(
    (updatedPlatePair: PlatePair) => {
      editedPlatePairRef.current = updatedPlatePair;
      const updatedComponent: Component = {
        ...component,
        platePairs: [
          ...component.platePairs.filter((platePair) => platePair.guid !== updatedPlatePair.guid),
          updatedPlatePair,
        ],
      };
      setEditedComponent(updatedComponent);
      throttledAnalyzeSelectedPlates(updatedComponent);
    },
    [component, setEditedComponent, throttledAnalyzeSelectedPlates],
  );

  const startRotation = useCallback(() => dispatch(setEditInProgress('EditingPlatePair')), [dispatch]);
  const rotate = useCallback(
    (angle: number) => {
      const { angle: snappedAngle, snapTo } = snapAngle(angle, component, platePair);
      const snappedAngleInDegrees = convertRadiansToDegrees(snappedAngle);

      setAngleSnap(snapTo);
      setSnappedAngle(snappedAngleInDegrees);

      const currentPlatePair = editedPlatePairRef.current ?? platePair;
      updateEditedPlatePair({ ...currentPlatePair, angleZ: snappedAngleInDegrees });
    },
    [component, platePair, updateEditedPlatePair],
  );
  const endRotate = useCallback(() => {
    if (editedPlatePairRef.current) {
      dispatch(upsertPlatePairs({ componentDesignGuid, platePairs: [editedPlatePairRef.current], lockPlating: true }));
      editedPlatePairRef.current = null;
    }

    setAngleSnap(null);
    dispatch(setEditInProgress(false));
  }, [componentDesignGuid, dispatch]);

  const shift = useCallback(
    (deltaX: number, deltaY: number) => {
      const currentPlatePair = editedPlatePairRef.current ?? platePair;
      updateEditedPlatePair({
        ...platePair,
        centerX: currentPlatePair.centerX + deltaX,
        centerY: currentPlatePair.centerY + deltaY,
      });
    },
    [platePair, updateEditedPlatePair],
  );
  const endShift = useCallback(() => {
    if (editedPlatePairRef.current) {
      dispatch(upsertPlatePairs({ componentDesignGuid, platePairs: [editedPlatePairRef.current], lockPlating: true }));
      editedPlatePairRef.current = null;
    }
  }, [componentDesignGuid, dispatch]);

  if (platePair.platePlacement === 'TopBottom') return null;

  const { centerX, centerY, centerZ } = platePair;
  const position = new Vector3(centerX, centerY, centerZ);
  const nudgeHandlePosition = position.clone().setX(centerX - 3.5);
  return (
    <>
      <NudgeHandle
        editType="EditingPlatePair"
        leftRight
        topBottom
        moveCallback={shift}
        postEditCallback={endShift}
        position={nudgeHandlePosition}
        size={2}
        coordinateUpDirection={coordinateUpDirection}
        settingsButton={{ type: 'FullTransform', position: { x: 7, y: 0, z: 0 }, size: 1.5 }}
      />
      <RotateHandle
        radius={1.4}
        size={1}
        angle={platePair.angleZ}
        anchorPoint={position}
        onStartMoveCallBack={startRotation}
        onRotateCallBack={rotate}
        onEndMoveCallBack={endRotate}
      />
      {angleSnap === 'axis' && angleEquals(snappedAngle, platePair.angleZ) && (
        <group position={position} rotation-z={convertDegreesToRadians(platePair.angleZ)}>
          <BoundingBoxCrossVisual box={box2FromSize(platePair.length, platePair.width)} />
        </group>
      )}
      {angleSnap && typeof angleSnap === 'object' && angleEquals(snappedAngle, platePair.angleZ) && (
        <MemberMidlineVisual member={angleSnap} component={component} />
      )}
    </>
  );
}

const snappingAngleAmount = convertDegreesToRadians(10);
function snapAngle(
  plateAngle: number,
  component: Component,
  platePair: PlatePair,
): { angle: number; snapTo: Member | 'axis' | null } {
  const members = findMembersWhichTouchPlatePair(component, platePair);

  const memberMidlineSegments = members
    .map((member) => ({ member, midline: calculateMemberMidline(member, component) }))
    .filter(
      (
        midlineSegmentWithMember,
      ): midlineSegmentWithMember is {
        member: Member;
        midline: Segment3D;
      } => !!midlineSegmentWithMember.midline,
    );

  const memberMidlineAngles = memberMidlineSegments.map((midlineSegmentWithGuid) => {
    const midlineVector = newVector3(midlineSegmentWithGuid.midline.end)
      .sub(newVector3(midlineSegmentWithGuid.midline.start))
      .normalize();
    return { member: midlineSegmentWithGuid.member, angle: vector2dToAngle(midlineVector) };
  });

  const axisAnglesCloseEnough: { angleDifference: number }[] = [];
  const midlineAnglesCloseEnough: { member: Member; angleDifference: number }[] = [];
  for (const memberMidlinePair of memberMidlineAngles) {
    const angleDifference = angleFromAxis(plateAngle - memberMidlinePair.angle);
    if (Math.abs(angleDifference) < snappingAngleAmount / 2) {
      midlineAnglesCloseEnough.push({ angleDifference, member: memberMidlinePair.member });
    }
  }
  if (midlineAnglesCloseEnough.length > 0) {
    midlineAnglesCloseEnough.sort((a, b) => Math.abs(a.angleDifference) - Math.abs(b.angleDifference));
    const closestResult = midlineAnglesCloseEnough[0];
    return { angle: radianInBounds(plateAngle - closestResult.angleDifference), snapTo: closestResult.member };
  }
  const angleDifference = angleFromAxis(plateAngle);
  if (Math.abs(angleDifference) < snappingAngleAmount / 2) {
    axisAnglesCloseEnough.push({ angleDifference });
  }
  if (axisAnglesCloseEnough.length > 0) {
    axisAnglesCloseEnough.sort((a, b) => Math.abs(a.angleDifference) - Math.abs(b.angleDifference));
    const closestResult = axisAnglesCloseEnough[0];
    return { angle: radianInBounds(plateAngle - closestResult.angleDifference), snapTo: 'axis' };
  }
  return { angle: plateAngle, snapTo: null };
}

const THICKNESS = 0.3;
const BoundingBoxCrossVisual = ({ box }: { box: Box2 }) => {
  const MATERIAL = <meshBasicMaterial transparent depthTest={false} depthWrite={false} color={0x0511c2} />;

  const center = box.getCenter(new Vector2());
  const size = box.getSize(new Vector2());
  return (
    <group position={[center.x, center.y, 0]} renderOrder={RenderOrder.PlatePairEditorHandle}>
      <mesh scale={[THICKNESS, size.y, 1]}>
        <planeGeometry />
        {MATERIAL}
      </mesh>
      <mesh scale={[size.x, THICKNESS, 1]}>
        <planeGeometry />
        {MATERIAL}
      </mesh>
    </group>
  );
};

const MemberMidlineVisual = ({ member, component }: { member: Member; component: Component }) => {
  const midline = calculateMemberMidline(member, component);
  if (!midline) {
    console.warn('Member midline does not exist for provided guid');
    return null;
  }

  const vector = newVector3(midline.end).sub(newVector3(midline.start)).normalize();
  const center = newVector3(midline.end).add(newVector3(midline.start)).multiplyScalar(0.5);
  const angle = radianInBounds(vector2dToAngle(vector));
  const length = newVector3(midline.end).distanceTo(newVector3(midline.start));

  return (
    <mesh
      position={center}
      scale={[THICKNESS, length, 1]}
      rotation-z={angle}
      renderOrder={RenderOrder.PlatePairEditorHandle}
    >
      <planeGeometry />
      <meshBasicMaterial transparent depthTest={false} depthWrite={false} color={0xaaaa00} />
    </mesh>
  );
};

const angleFromAxis = (angle: number) => {
  const reducedAngle = radianInBounds(angle - Math.PI / 4) % (Math.PI / 2);
  return reducedAngle - Math.PI / 4;
};

const vector2dToAngle = (vector: { x: number; y: number }) => Math.atan2(-vector.x, vector.y);

function radianInBounds(radians: number) {
  let result = radians % (2 * Math.PI);
  if (result < 0) result += 2 * Math.PI;
  return result;
}
